import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Row,
  Spin,
  Typography,
  message,
} from 'antd';
import {
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import Moment from 'moment';
import { useSelector } from 'react-redux';
import { getTiposDeAplicacion } from 'api/recaudacion';
import { MAX_CUENTAS } from 'api/procesosMasivos/saldosMasivos';
import {
  ESTATUS_DE_TAREA,
  PERMITE_VARIOS_DESCUENTOS,
  aplicarDescuentosMasivos,
  cancelarDescuentosMasivos,
  esCuentaDesactivada,
  getEstatusAplicacionMasiva,
  getTiposDeDescuentoParaAplicacionMasiva,
} from 'api/procesosMasivos/descuentosMasivos';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import Table from 'components/Table';
import usePermissionMessage from 'hooks/usePermissionMessage';
import { logout } from 'utils/api';
import toCurrency from 'utils/toCurrency';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import LoteDeCuentas, {
  MSG_MAXIMO,
  MSG_SIN_PERMISO,
  useLoteDeCuentas,
} from './components/LoteDeCuentas';
import ModalCapturaDescuentos from './components/ModalCapturaDescuentos';
import ModalCancelarDescuentosMasivos from './components/ModalCancelarDescuentosMasivos';
import ModalConfirmacionConContrasena from './components/ModalConfirmacionConContrasena';
import useEstatusDeTarea from './components/useEstatusDeTarea';

// TODO: permisos provisionales (mismos que la captura manual de descuentos) hasta que
// backend defina un permiso propio para el proceso masivo
export const permissionConfig = {
  mainPermission: 'view_tipodedescuento',
  required: ['view_predio'],
  customActions: [
    {
      name: 'aplicar',
      permissions: ['view_tipodedescuento', 'view_predio', 'add_descuento'],
    },
    {
      name: 'cancelar',
      permissions: ['view_tipodedescuento', 'view_predio', 'change_descuento'],
    },
  ],
};

const MSG_LOTE_VACIO = 'Debe agregar al menos una cuenta';
const MSG_SIN_DESCUENTO = 'Debe seleccionar al menos un descuento';
const MSG_EN_PROCESO = 'Hay una aplicación de descuentos en proceso, espere a que termine';
const MSG_INICIADO = 'Procesamiento de descuentos masivos iniciado correctamente';
const MSG_ALERTA_APLICACION = 'Esta acción crea descuentos reales en las cuentas y puede tardar varios minutos en completarse. El resultado también llegará por correo.';

const initialValues = { tipo_de_padron: padronesEnum.PREDIO };

const formatNumero = (valor) => (valor ?? 0).toLocaleString('es-MX');
// Solo se enlazan URLs https que regresa el backend (el Excel trae datos de contribuyentes)
const esUrlSegura = (url) => typeof url === 'string' && /^https:\/\//i.test(url);

const DescuentosMasivos = ({ permission }) => {
  const puedeOperar = !!permission.hasAllViewPermission;
  const puedeAplicar = puedeOperar && !!permission.aplicar;
  const puedeCancelar = puedeOperar && !!permission.cancelar;
  usePermissionMessage(permission);

  const [form] = Form.useForm();
  const lote = useLoteDeCuentas({ form, puedeOperar });
  const tarea = useEstatusDeTarea(getEstatusAplicacionMasiva);
  const tiposDeAplicacion = useSelector(({ catalogs }) => catalogs.tiposDeAplicacion) || [];

  const [loading, setLoading] = useState(false);
  const [tiposDeDescuento, setTiposDeDescuento] = useState([]);
  const [selectedDescuentosKeys, setSelectedDescuentosKeys] = useState([]);
  const [visibleCaptura, setVisibleCaptura] = useState(false);
  const [visibleConfirmacion, setVisibleConfirmacion] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  // Descuentos y observaciones capturados, pendientes de confirmar con contraseña
  const [capturaPendiente, setCapturaPendiente] = useState(null);

  const descuentosSeleccionados = tiposDeDescuento
    .filter((d) => selectedDescuentosKeys.includes(d.id));

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await getTiposDeAplicacion(true);
      const _tiposDeDescuento = await getTiposDeDescuentoParaAplicacionMasiva();
      if (mounted) {
        setTiposDeDescuento(_tiposDeDescuento);
        setLoading(false);
      }
    };
    if (puedeOperar) fetchAll();
    return () => { mounted = false; };
  }, [puedeOperar]);

  /**
   * Validaciones comunes antes de aplicar o cancelar
   * @returns {boolean}
   */
  const validarLote = () => {
    const { cuentasDelLote } = lote;
    if (!cuentasDelLote.length) {
      message.warning(MSG_LOTE_VACIO);
      return false;
    }
    if (cuentasDelLote.length > MAX_CUENTAS) {
      message.warning(MSG_MAXIMO);
      return false;
    }
    return true;
  };

  const onClickAplicar = () => {
    if (!puedeAplicar) {
      message.warning(MSG_SIN_PERMISO);
      return;
    }
    if (tarea.procesando) {
      message.warning(MSG_EN_PROCESO);
      return;
    }
    if (!validarLote()) return;
    if (!selectedDescuentosKeys.length) {
      message.warning(MSG_SIN_DESCUENTO);
      return;
    }
    setVisibleCaptura(true);
  };

  /**
   * Si el backend desactivó la cuenta por exceder los intentos de contraseña,
   * cierra los modales y la sesión
   * @param {{ok: boolean, error?: string}} resultado
   * @returns {boolean} true si la cuenta fue desactivada
   */
  const manejarCuentaDesactivada = (resultado) => {
    if (resultado.ok || !esCuentaDesactivada(resultado.error)) return false;
    setVisibleConfirmacion(false);
    setVisibleCaptura(false);
    setVisibleCancelar(false);
    message.error(resultado.error, 8);
    logout();
    return true;
  };

  // Al regresar de la confirmación se conserva la captura
  const onAplicar = (captura) => {
    setCapturaPendiente(captura);
    setVisibleConfirmacion(true);
  };

  const onConfirmarAplicacion = async (password) => {
    const resultado = await aplicarDescuentosMasivos({
      cuentas: lote.cuentasDelLote,
      descuentos: capturaPendiente.descuentos,
      comentarios: capturaPendiente.comentarios,
      password,
    });
    if (manejarCuentaDesactivada(resultado)) return resultado;
    if (resultado.ok && resultado.data?.task_id) {
      setVisibleConfirmacion(false);
      setVisibleCaptura(false);
      setCapturaPendiente(null);
      message.success(MSG_INICIADO);
      tarea.iniciar(resultado.data.task_id);
    }
    return resultado;
  };

  const onClickCancelar = () => {
    if (!puedeCancelar) {
      message.warning(MSG_SIN_PERMISO);
      return;
    }
    if (!validarLote()) return;
    if (!selectedDescuentosKeys.length) {
      message.warning(MSG_SIN_DESCUENTO);
      return;
    }
    setVisibleCancelar(true);
  };

  const onCancelar = async ({ motivo, password }) => {
    const resultado = await cancelarDescuentosMasivos({
      cuentas: lote.cuentasDelLote,
      tipos_de_descuento: selectedDescuentosKeys,
      motivo_de_cancelacion: motivo,
      password,
    });
    manejarCuentaDesactivada(resultado);
    return resultado;
  };

  const limpiar = () => {
    lote.limpiar();
    setSelectedDescuentosKeys([]);
    if (!tarea.procesando) tarea.reiniciar();
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 100,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 500,
    },
    {
      titleText: 'Tipo de Aplicación',
      dataIndex: 'tipo_de_aplicacion',
      key: 'tipo_de_aplicacion',
      width: 200,
      render: (val) => tiposDeAplicacion.find((item) => item.id === val)?.descripcion,
    },
    {
      titleText: 'Rango de Fechas',
      dataIndex: 'el_descuento_es_por_rango_de_fechas',
      key: 'el_descuento_es_por_rango_de_fechas',
      width: 250,
      render: (val, record) => (val && record?.fecha_inicio && record?.fecha_fin
        ? `${Moment(record.fecha_inicio).format('DD/MM/YYYY')} - ${Moment(record.fecha_fin).format('DD/MM/YYYY')}`
        : 'N/A'),
    },
  ].map((col) => ({ ...col, title: col.titleText }));

  const rowSelection = {
    type: PERMITE_VARIOS_DESCUENTOS ? 'checkbox' : 'radio',
    selectedRowKeys: selectedDescuentosKeys,
    onChange: (keys) => setSelectedDescuentosKeys(keys),
    getCheckboxProps: () => ({ disabled: !puedeOperar }),
  };

  const toggleDescuento = (record) => {
    if (!puedeOperar) return;
    if (!PERMITE_VARIOS_DESCUENTOS) {
      setSelectedDescuentosKeys([record.id]);
      return;
    }
    setSelectedDescuentosKeys((prev) => (prev.includes(record.id)
      ? prev.filter((k) => k !== record.id) : [...prev, record.id]));
  };

  // Resumen de lo que se va a aplicar: cuántas cuentas, de dónde sale el lote y qué descuentos
  const renderMensajeConfirmacion = () => {
    const desdeArchivo = !!lote.archivoCuentas;
    return (
      <>
        <Typography.Paragraph>
          {`¿Estás seguro de querer aplicar los siguientes descuentos a ${formatNumero(lote.cuentasDelLote.length)} cuenta(s) ${desdeArchivo ? 'del archivo cargado' : 'agregadas por búsqueda'}?`}
        </Typography.Paragraph>
        <ul>
          {descuentosSeleccionados.map((descuento) => (
            <li key={descuento.id}>
              <Typography.Text strong>
                {`${descuento.clave} - ${descuento.descripcion}`}
              </Typography.Text>
            </li>
          ))}
        </ul>
        {desdeArchivo && !!lote.cuentas.length && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Hay un archivo cargado: las ${formatNumero(lote.cuentas.length)} cuenta(s) agregadas por búsqueda no se incluyen.`}
          />
        )}
      </>
    );
  };

  const renderResultado = () => {
    if (tarea.estatus === ESTATUS_DE_TAREA.PROCESANDO) {
      return (
        <Alert
          type="info"
          showIcon
          icon={<Spin size="small" />}
          message="Aplicando descuentos..."
          description="El proceso continúa aunque salga de esta pantalla. Al terminar, recibirá un correo con el archivo de resultados."
        />
      );
    }
    if (tarea.estatus === ESTATUS_DE_TAREA.ERROR) {
      return <Alert type="error" showIcon message={tarea.error} />;
    }
    const { resultado } = tarea;
    if (!resultado) return null;
    return (
      <>
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Aplicación de "${resultado.tipo_de_descuento}" terminada. El archivo también se envió por correo.`}
        />
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="Cuentas solicitadas">
            {formatNumero(resultado.total_de_cuentas)}
          </Descriptions.Item>
          <Descriptions.Item label="Cuentas con descuento">
            {formatNumero(resultado.cuentas_con_descuento)}
          </Descriptions.Item>
          <Descriptions.Item label="Descuentos aplicados">
            {formatNumero(resultado.descuentos_aplicados)}
          </Descriptions.Item>
          <Descriptions.Item label="Sin cargos aplicables">
            {formatNumero(resultado.cuentas_sin_cargos_aplicables)}
          </Descriptions.Item>
          <Descriptions.Item label="Sin adeudo">
            {formatNumero(resultado.cuentas_sin_adeudo)}
          </Descriptions.Item>
          <Descriptions.Item label="No encontradas">
            {formatNumero(resultado.cuentas_no_encontradas)}
          </Descriptions.Item>
          <Descriptions.Item label="Con error">
            {formatNumero(resultado.cuentas_con_error)}
          </Descriptions.Item>
          <Descriptions.Item label="Saldo total (con descuento)">
            {toCurrency(resultado.saldo_total || 0)}
          </Descriptions.Item>
        </Descriptions>
        <Row gutter={10} justify="end" style={{ marginTop: 12 }}>
          {esUrlSegura(resultado.url_de_errores) && (
            <Col>
              <Button
                icon={<DownloadOutlined />}
                href={resultado.url_de_errores}
                target="_blank"
                rel="noopener noreferrer"
              >
                Descargar errores
              </Button>
            </Col>
          )}
          {esUrlSegura(resultado.url_descarga) && (
            <Col>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                href={resultado.url_descarga}
                target="_blank"
                rel="noopener noreferrer"
              >
                Descargar Excel
              </Button>
            </Col>
          )}
        </Row>
      </>
    );
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin spinning={loading || lote.loading} tip="Cargando...">
        <Card
          className="form-container"
          bordered={false}
          title="Búsqueda del Padrón"
          extra={(
            <FormSubmitCustomControls
              loading={loading || lote.loading}
              controls={[{
                text: 'Limpiar Búsqueda',
                icon: <DeleteOutlined />,
                onClick: limpiar,
              }]}
            />
          )}
        >
          <LoteDeCuentas
            lote={lote}
            permission={permission}
            formName="descuentos-masivos"
            initialValues={initialValues}
            permitirQuitarCuentas
            permitirSeleccionarTodos
          />

          <br />
          <Card type="inner" title="Descuentos Disponibles">
            <Table
              cols={columns}
              mobileCols={[columns[1]]}
              data={tiposDeDescuento}
              rowKey="id"
              rowSelection={rowSelection}
              handleOnRowClick={toggleDescuento}
              allowAdd={false}
              allowEdit={false}
              allowDelete={false}
              allowSearch={false}
              hasAllViewPermission={puedeOperar}
              permission={permission}
              customActions={[
                {
                  text: 'Aplicar Descuento',
                  icon: <PlusOutlined />,
                  onClick: onClickAplicar,
                  hasPermission: puedeAplicar,
                  disabled: tarea.procesando,
                  disabledBySelection: false,
                },
                {
                  text: 'Cancelar Descuentos',
                  icon: <CloseCircleOutlined />,
                  onClick: onClickCancelar,
                  hasPermission: puedeCancelar,
                  disabledBySelection: false,
                },
              ]}
            />
          </Card>

          {!!tarea.estatus && (
            <>
              <br />
              <Card type="inner" title="Resultado de la aplicación">
                {renderResultado()}
              </Card>
            </>
          )}
        </Card>

        <ModalCapturaDescuentos
          visible={visibleCaptura}
          descuentos={descuentosSeleccionados}
          onRegresar={() => setVisibleCaptura(false)}
          onAplicar={onAplicar}
        />
        <ModalCancelarDescuentosMasivos
          visible={visibleCancelar}
          descuentos={descuentosSeleccionados}
          totalDeCuentas={lote.cuentasDelLote.length}
          onRegresar={() => setVisibleCancelar(false)}
          onCancelar={onCancelar}
        />
        <ModalConfirmacionConContrasena
          visible={visibleConfirmacion}
          mensaje={renderMensajeConfirmacion()}
          alerta={MSG_ALERTA_APLICACION}
          onRegresar={() => setVisibleConfirmacion(false)}
          onConfirmar={onConfirmarAplicacion}
        />
      </Spin>
    </Row>
  );
};

DescuentosMasivos.propTypes = {
  permission: PropTypes.shape({
    hasAllViewPermission: PropTypes.bool,
    aplicar: PropTypes.bool,
    cancelar: PropTypes.bool,
  }).isRequired,
};

export default DescuentosMasivos;
